import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Pagination,
  CircularProgress,
} from "@mui/material";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import CustomText, { textStyles } from "../../../components/CustomText";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { BASE_URL } from "../../../components/BaseUrl";
import { decryptToken } from "../../../Utlis/Encryption";
import CustomButton from "../../../components/CustomButton";
import empty from "../../../assets/empty.png";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

// Returns the notification's title/message in the current app language,
// falling back to the base (untranslated) field when no *_en/*_es value exists.
const getLocalizedField = (item, field, lang) => {
  if (!item) return "";
  const suffix = lang === "es" ? "es" : "en";
  return item[`${field}_${suffix}`] || item[field] || "";
};

const Notifications = () => {
  const { t, i18n } = useTranslation("");
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;
  const limit = 5;

 const fetchNotifications = async (pageNum = 1) => {
  setLoading(true);
  try {
    const response = await fetch(
      `${BASE_URL}/notifications/account-executive?page=${pageNum}&limit=${limit}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) throw new Error("Failed to fetch notifications");

    const data = await response.json();

    if (!data.error) {
      const { notifications, pagination } = data.data;

      setNotifications(notifications);
      setTotalPages(pagination.pages);
      setPage(pagination.page); // keep UI in sync
    } else {
      throw new Error(data.message || "Error fetching notifications");
    }
  } catch (error) {
    console.error("Error fetching notifications:", error);
    toast.error(error.message || "Error fetching notifications");
  } finally {
    setLoading(false);
  }
};



  // ------------------- Mark All As Read -------------------
  const markAllAsRead = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/notifications/account-executive/mark-all-read`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        toast.success(t("notification_screen.toast.markAsReadSuccess"));
        fetchNotifications(page);
      }
    } catch (error) {
      console.error("Error marking all as read:", error);
      toast.error(t("notification_screen.toast.markAsReadError"));
    }
  };

  // ------------------- Handle Notification Click -------------------
  const handleNotificationClick = async (notification) => {
    // Mark as read if not already
    if (!notification.is_read) {
      try {
        await fetch(
          `${BASE_URL}/notifications/account-executive/${notification.id}/mark-read`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        // Refresh notifications after marking as read
        fetchNotifications(page);
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    }

    // Navigate based on notification type
    if (notification.type === "commission_earned") {
      navigate("/commission-record", { state: { fromDashboard: true } });
    } 
      if (notification.type === "commission_paid") {
      navigate("/commission-record", { state: { fromDashboard: true } });
    } 
      if (notification.type === "new_message") {
      navigate("/messages", { state: { fromDashboard: true } });
    } 
  };

  useEffect(() => {
    fetchNotifications(page);
  }, [page]);

  // ------------------- UI -------------------
  return (
    <Box sx={{ px: 2, mt: 1 }}>
      <Box
        sx={{
          width: "100%",
          backgroundColor: "white",
          border: "2px solid #091E4224",
          borderRadius: 2,
          py: 2,
          minHeight: "70vh",
          position: "relative",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #E0E0E0",
            px: 2,
            pb: 2,
            mb: 2,
          }}
        >
          <CustomText
            sx={{
              ...textStyles.h1,
              fontWeight: 600,
              fontSize: "18px",
              color: "#003149",
            }}
          >
            {t("notification_screen.title") || "Notifications"}
          </CustomText>

          <IconButton
            onClick={markAllAsRead}
            sx={{
              backgroundColor: "#006EC2",
              color: "white",
              px: 2,
              borderRadius: "6px",
              fontSize: 14,
              fontFamily: 'Poppins, sans-serif',

              "&:hover": { backgroundColor: "#005bb5" },
            }}
          >
            <MarkEmailReadOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
            {t("markAsRead")}
          </IconButton>
        </Box>

        {/* Loader */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "50vh",
            }}
          >
            <CircularProgress size={40} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 5,
            }}
          >
            <img
              src={empty}
              alt="No Data"
              style={{ width: 200, marginBottom: 16 }}
            />
            <CustomText
              sx={{
                ...textStyles.h1,
                fontWeight: 500,
                fontSize: "16px",
                color: "#003149",
              }}
            >
              {t("empty.no_user")}
            </CustomText>
          </Box>
        ) : (
          <>
            {/* Notification Table */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                px: 2,
              }}
            >
              {notifications.map((n, i) => (
                <Box
                  key={i}
                  onClick={() => handleNotificationClick(n)}
                  sx={{
                    backgroundColor: !n.is_read ? "#EAF4FF" : "#fff",
                    border: "1px solid #DADADA",
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0px 1px 2px rgba(0,0,0,0.05)",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: !n.is_read ? "#E0ECFF" : "#F5F5F5",
                      boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "16px",
                        color: "#212121",
                        fontFamily: "Poppins, sans-serif",
                      }}
                    >
                      {getLocalizedField(n, "title", i18n.language)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: "#555",
                        mt: 0.5,
                        fontFamily: "Poppins, sans-serif",
                      }}
                    >
                      {getLocalizedField(n, "message", i18n.language)}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#5A6474",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    {new Date(n.sent_at).toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, val) => setPage(val)}

                  shape="rounded"
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Notifications;
